import { useEffect, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import MoneyInput from "@/components/MoneyInput";
import IbanInput from "@/components/IbanInput";

const empty = { company_id: "", banka: "", sube: "", iban: "", hesap_adi: "", bakiye: 0, currency: "TRY" };

export default function BanksPage() {
  const { companies, selectedCompany } = useCompany();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    const q = selectedCompany === "all" ? "" : `?company_id=${selectedCompany}`;
    const { data } = await api.get(`/banks${q}`);
    setItems(data);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedCompany]);

  const openNew = () => {
    setForm({ ...empty, company_id: selectedCompany !== "all" ? selectedCompany : companies[0]?.id || "" });
    setEditingId(null); setDialog(true);
  };
  const openEdit = (b) => { setForm({ ...b }); setEditingId(b.id); setDialog(true); };

  const save = async () => {
    if (!form.company_id) { toast.error("Şirket seçin"); return; }
    if (editingId) await api.put(`/banks/${editingId}`, form);
    else await api.post("/banks", form);
    toast.success("Kaydedildi");
    setDialog(false); load();
  };
  const remove = async (id) => {
    if (!confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/banks/${id}`); load();
  };

  const totals = items.reduce((acc, b) => {
    acc[b.currency] = (acc[b.currency] || 0) + Number(b.bakiye || 0);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Banka Hesapları</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Şirketlerinizin banka hesapları ve güncel bakiyeleri.
          </p>
        </div>
        <Button onClick={openNew} data-testid="add-bank-btn"><Plus size={16} className="mr-2"/>Yeni Hesap</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {Object.entries(totals).map(([cur, val]) => (
          <Card key={cur} className="border-border">
            <CardContent className="p-6">
              <div className="text-xs uppercase tracking-wider text-muted-foreground">Toplam {cur}</div>
              <div className="font-heading font-extrabold text-2xl tabular-nums mt-1">{formatMoney(val, cur)}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Banka</TableHead><TableHead>Şube</TableHead>
                <TableHead>Hesap Adı</TableHead><TableHead>IBAN</TableHead>
                <TableHead className="text-right">Bakiye</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Banka hesabı bulunmuyor.
                </TableCell></TableRow>
              )}
              {items.map((b) => (
                <TableRow key={b.id} data-testid={`bank-row-${b.id}`}>
                  <TableCell className="font-medium">{b.banka}</TableCell>
                  <TableCell>{b.sube}</TableCell>
                  <TableCell>{b.hesap_adi}</TableCell>
                  <TableCell className="tabular-nums text-xs">{b.iban}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium">{formatMoney(b.bakiye, b.currency)}</TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(b)}><PencilSimple size={15}/></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(b.id)}><Trash size={15} className="text-destructive"/></Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Hesabı Düzenle" : "Yeni Banka Hesabı"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>Şirket</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seç..." /></SelectTrigger>
                <SelectContent>
                  {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Banka</Label><Input value={form.banka} onChange={(e) => setForm({ ...form, banka: e.target.value })} /></div>
            <div><Label>Şube</Label><Input value={form.sube} onChange={(e) => setForm({ ...form, sube: e.target.value })} /></div>
            <div><Label>Hesap Adı</Label><Input value={form.hesap_adi} onChange={(e) => setForm({ ...form, hesap_adi: e.target.value })} /></div>
            <div><Label>IBAN</Label><IbanInput value={form.iban} onChange={(e) => setForm({ ...form, iban: e.target.value })} /></div>
            <div>
              <Label>Bakiye</Label>
              <MoneyInput value={form.bakiye}
                onChange={(n) => setForm({ ...form, bakiye: n })} />
            </div>
            <div>
              <Label>Para Birimi</Label>
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["TRY","USD","EUR"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>İptal</Button>
            <Button onClick={save} data-testid="bank-save-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
